import { gettext } from './utils.js';

/**
 * Email Verification Module for Tāmēt.lv
 * Handles client-side verification functionality
 */

class EmailVerification {
    constructor() {
        this.init();
    }

    init() {
        this.bindEvents();
        this.handleVerificationStatus();
    }

    bindEvents() {
        // Handle resend verification form submissions
        const resendForms = document.querySelectorAll('[data-verification-resend]');
        resendForms.forEach(form => {
            form.addEventListener('submit', this.handleResendSubmission.bind(this));
        });

        // Handle verification link clicks (for single-page app behavior)
        const verificationLinks = document.querySelectorAll('[data-verification-link]');
        verificationLinks.forEach(link => {
            link.addEventListener('click', this.handleVerificationClick.bind(this));
        });
    }

    handleResendSubmission(event) {
        const form = event.target;
        const submitButton = form.querySelector('button[type="submit"]');
        
        if (submitButton) {
            // Disable button and show loading state
            submitButton.disabled = true;
            const originalText = submitButton.textContent;
            submitButton.textContent = gettext('Sending...');
            
            // Re-enable after 3 seconds to prevent multiple rapid submissions
            setTimeout(() => {
                submitButton.disabled = false;
                submitButton.textContent = originalText;
            }, 3000);
        }
    }

    handleVerificationClick(event) {
        const link = event.target;
        
        // Add loading state to verification link
        if (link.classList.contains('verify-button')) {
            link.textContent = gettext('Verifying...');
            link.style.opacity = '0.7';
        }
    }

    handleVerificationStatus() {
        // Check if we're on a verification page and show appropriate messaging
        const verificationPages = [
            'verification-sent',
            'verification-success',
            'verification-failed',
            'resend-verification'
        ];

        const currentPage = this.getCurrentPageType();
        
        if (verificationPages.includes(currentPage)) {
            this.enhanceVerificationPage(currentPage);
        }
    }

    getCurrentPageType() {
        // Determine current page type based on URL or body class
        const path = window.location.pathname;
        
        if (path.includes('verification-sent')) return 'verification-sent';
        if (path.includes('verification-success')) return 'verification-success';
        if (path.includes('verification-failed')) return 'verification-failed';
        if (path.includes('resend-verification')) return 'resend-verification';
        
        return null;
    }

    enhanceVerificationPage(pageType) {
        switch (pageType) {
            case 'verification-sent':
                this.addEmailCheckReminder();
                break;
            case 'verification-success':
                this.addSuccessAnimation();
                break;
            case 'verification-failed':
                this.addRetryGuidance();
                break;
            case 'resend-verification':
                this.addResendGuidance();
                break;
        }
    }

    addEmailCheckReminder() {
        // Add periodic reminder to check email
        let reminderCount = 0;
        const maxReminders = 3;
        
        const showReminder = () => {
            if (reminderCount < maxReminders) {
                reminderCount++;
                this.showNotification(
                    gettext("Don't forget to check your email (including spam folder) for the verification link!"),
                    'info'
                );
            }
        };

        // Show reminder every 2 minutes
        setTimeout(showReminder, 120000); // 2 minutes
        setTimeout(showReminder, 300000); // 5 minutes
        setTimeout(showReminder, 600000); // 10 minutes
    }

    addSuccessAnimation() {
        // Add a subtle success animation to the check icon
        const successIcon = document.querySelector('.text-green-600 svg');
        if (successIcon) {
            successIcon.style.animation = 'pulse 2s infinite';
        }
    }

    addRetryGuidance() {
        // Add helpful guidance for common issues
        const guidanceText = document.createElement('div');
        guidanceText.className = 'mt-4 text-sm text-gray-600 text-center';
        guidanceText.innerHTML = `
            <p><strong>${gettext('Tip:')}</strong> ${gettext("If you're having trouble, try:")}</p>
            <ul class="mt-2 text-left list-disc list-inside space-y-1">
                <li>${gettext('Copying the full verification URL from your email')}</li>
                <li>${gettext('Opening the link in a new browser tab')}</li>
                <li>${gettext('Requesting a new verification email')}</li>
            </ul>
        `;
        
        const container = document.querySelector('.max-w-md');
        if (container) {
            container.appendChild(guidanceText);
        }
    }

    addResendGuidance() {
        // Add guidance about resending verification emails
        const form = document.querySelector('form');
        if (form) {
            const guidance = document.createElement('div');
            guidance.className = 'text-xs text-gray-500 mt-2';
            guidance.textContent = gettext('Make sure to enter the same email address you used during registration.');
            
            const emailInput = form.querySelector('input[type="email"]');
            if (emailInput && emailInput.parentNode) {
                emailInput.parentNode.appendChild(guidance);
            }
        }
    }

    showNotification(message, type = 'info') {
        // Create a simple notification
        const notification = document.createElement('div');
        notification.className = `fixed top-4 right-4 p-4 rounded-md shadow-lg z-50 ${
            type === 'info' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
            type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
            'bg-red-100 text-red-800 border border-red-200'
        }`;
        notification.textContent = message;
        
        document.body.appendChild(notification);
        
        // Auto-remove after 5 seconds
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 5000);
    }

    // Utility method to validate email format
    isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    // Method to handle verification timeout warnings
    addVerificationTimeout() {
        // Add a countdown timer for verification link expiration
        const expirationTime = new Date().getTime() + (24 * 60 * 60 * 1000); // 24 hours
        
        const updateTimer = () => {
            const now = new Date().getTime();
            const timeLeft = expirationTime - now;
            
            if (timeLeft > 0) {
                const hours = Math.floor(timeLeft / (1000 * 60 * 60));
                const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
                
                const timerElement = document.getElementById('verification-timer');
                if (timerElement) {
                    timerElement.textContent = `Link expires in ${hours}h ${minutes}m`;
                }
            }
        };
        
        // Update timer every minute
        setInterval(updateTimer, 60000);
        updateTimer(); // Initial call
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    new EmailVerification();
});

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
    module.exports = EmailVerification;
} 